import React, { useMemo } from 'react';
import { Avatar, AvatarGroup, Box, Card, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { ChevronRight, CurrencyRupee, Folder, Interests } from '@mui/icons-material';
import { computeGroupTotals, getCategoryLabel, getCreatedByName, getPlatformNames, initials, uniqueValues } from './campaignFieldUtils';

// The card representation of a campaign group: campaigns inside it keep
// their own budget/POC/status untouched, this just rolls those numbers up
// so a bundle of related campaigns reads as one line item in the grid
// instead of N separate cards.
const GroupCard = React.memo(function GroupCard({ group, onOpen, disabled = false, isSuperUser = false }) {
    const members = group.campaigns;

    const { totalBudget, totalLimit, totalNetRevenue, pocs, categories, platforms, memberCount } = useMemo(() => {
        const pocNames = [];
        const categoryLabels = [];
        const platformNames = [];
        // A campaign deleted elsewhere stays referenced in the group doc
        // (deleting doesn't pull it out of every group it's in) — exclude
        // it here the same way the drill-down view does, so the count
        // doesn't include something no longer real. computeGroupTotals
        // applies the identical exclusion for the budget/limit rollup.
        const list = (members || []).filter((c) => !c.isDeleted);

        list.forEach((member) => {
            pocNames.push(getCreatedByName(member));
            categoryLabels.push(getCategoryLabel(member));
            platformNames.push(...getPlatformNames(member));
        });

        return {
            // { campaigns: members } rather than the raw group prop — keeps
            // this depending on `members` alone, consistent with the rest of
            // the memo, rather than on `group`'s own (possibly less stable)
            // reference.
            ...computeGroupTotals({ campaigns: members }),
            pocs: uniqueValues(pocNames),
            categories: uniqueValues(categoryLabels),
            platforms: uniqueValues(platformNames),
            memberCount: list.length,
        };
    }, [members]);

    return (
        <Card
            sx={{
                position: 'relative',
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                height: '100%',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                border: '1px solid',
                borderColor: 'rgba(99, 102, 241, 0.25)',
                backgroundColor: 'rgba(99, 102, 241, 0.03)',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease',
                '&:hover': disabled
                    ? undefined
                    : {
                          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
                          transform: 'translateY(-1px)',
                      },
            }}
            onClick={disabled ? undefined : () => onOpen(group._id)}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
                    <Box
                        sx={{
                            color: '#fff',
                            backgroundColor: '#6366F1',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                        }}
                    >
                        <Folder fontSize="small" />
                    </Box>
                    <Box sx={{ marginLeft: 1.5, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        >
                            {group.name}
                        </Typography>
                        <Chip
                            label={`${memberCount} campaign${memberCount === 1 ? '' : 's'}`}
                            size="small"
                            sx={{
                                mt: 0.5,
                                height: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                                color: '#6366F1',
                            }}
                        />
                    </Box>
                </Box>
                <ChevronRight sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.5 }} />
            </Box>

            {categories.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.5} sx={{ mt: 1.5 }}>
                    {categories.slice(0, 3).map((c) => (
                        <Chip key={c} label={c} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    ))}
                    {categories.length > 3 && (
                        <Chip label={`+${categories.length - 3}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    )}
                </Stack>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5,
                    width: '100%',
                    mt: 2,
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        POCs
                    </Typography>
                    <AvatarGroup
                        max={4}
                        sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 11 }, justifyContent: 'flex-start' }}
                    >
                        {pocs.map((name) => (
                            <Tooltip key={name} title={name}>
                                <Avatar sx={{ bgcolor: '#94a3b8' }}>{initials(name)}</Avatar>
                            </Tooltip>
                        ))}
                    </AvatarGroup>
                </Box>

                <Stack direction="row" spacing={2.5} flexWrap="wrap" rowGap={1}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Total Budget
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            <CurrencyRupee sx={{ fontSize: '14px' }} />
                            {totalBudget.toLocaleString('en-IN')}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Total Limit
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            color="error"
                            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            <CurrencyRupee sx={{ fontSize: '14px' }} />
                            {totalLimit.toLocaleString('en-IN')}
                        </Typography>
                    </Box>
                    {isSuperUser && totalNetRevenue != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Net Revenue
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    color: totalNetRevenue >= 0 ? '#0AC380' : '#e34f4f',
                                }}
                            >
                                <CurrencyRupee sx={{ fontSize: '14px' }} />
                                {totalNetRevenue.toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ maxWidth: 160, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Platform
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontWeight: 700,
                                minWidth: 0,
                            }}
                        >
                            <Interests sx={{ fontSize: '14px', flexShrink: 0 }} color="action" />
                            {/* See CampaignCard's platform line: text-overflow on a
                                flex container is a no-op, so truncation needs its
                                own span with minWidth: 0. */}
                            <Box
                                component="span"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                }}
                            >
                                {platforms.join(', ') || '-'}
                            </Box>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
});

export default GroupCard;
